import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Zap, Brain, Target, TrendingUp, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OperationalIntelligence = () => {
  const navigate = useNavigate();

  const intelligenceFeatures = [
    {
      icon: Brain,
      title: 'Smart Analytics',
      description: 'AI-powered insights that identify patterns and predict potential food safety issues before they occur.'
    },
    {
      icon: Zap,
      title: 'Process Automation',
      description: 'Intelligent automation of routine tasks, notifications, and workflow management to improve efficiency.'
    },
    {
      icon: Target,
      title: 'Predictive Monitoring',
      description: 'Advanced algorithms that analyze historical data to forecast trends and recommend proactive measures.'
    },
    {
      icon: TrendingUp,
      title: 'Performance Optimization',
      description: 'Real-time recommendations for improving compliance rates, reducing waste, and enhancing operational efficiency.'
    }
  ];

  return (
    <div className="min-h-screen">
      <header className="glass-panel-nav">
        <div className="container mx-auto px-4 py-4">
          <Button 
            variant="glass" 
            onClick={() => navigate('/')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
          <h1 className="heading-hero">Operational Intelligence</h1>
          <p className="subheading mt-4">
            Smart automation and process optimization powered by advanced analytics
          </p>
        </div>
      </header>

      <main className="py-16">
        <div className="container mx-auto px-4">
          {/* Key Features */}
          <section className="mb-16">
            <h2 className="heading-section mb-8 text-center">Intelligence Capabilities</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {intelligenceFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <Card key={feature.title} className="glass-panel border-0 bg-transparent">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-xl bg-crystal-orange/10 border border-crystal-orange/20">
                          <IconComponent className="h-6 w-6 text-crystal-orange" />
                        </div>
                        <CardTitle className="text-white">{feature.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-white/70">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Use Cases */}
          <section className="mb-16">
            <div className="glass-panel-elevated p-8">
              <h2 className="heading-section mb-6 text-center">Real-World Applications</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="glass-panel p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Temperature Alerts</h3>
                    <p className="text-white/70">
                      Intelligent monitoring of temperature logs with predictive alerts for equipment failures.
                    </p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="glass-panel p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Compliance Forecasting</h3>
                    <p className="text-white/70">
                      Predict audit outcomes and identify areas needing attention before inspections.
                    </p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="glass-panel p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Resource Optimization</h3>
                    <p className="text-white/70">
                      Smart scheduling and resource allocation based on operational patterns and priorities.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center">
            <div className="glass-panel-elevated p-8">
              <h3 className="text-2xl font-bold text-white mb-4">
                Unlock the Power of Intelligent Operations
              </h3>
              <p className="text-white/70 mb-6 max-w-2xl mx-auto">
                Transform your food safety operations with AI-powered insights and automated workflows.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="cta-primary px-8 py-3">
                  See Demo
                </Button>
                <Button className="cta-secondary px-8 py-3">
                  Learn More
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default OperationalIntelligence;